// ── Contractor Rob Flooring — TechnicalDataSheetPage.js ────
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Download } from 'lucide-react';

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('[data-reveal]');
    els.forEach(el => el.setAttribute('data-reveal-init', ''));
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in'); }),
      { threshold: 0.08 }
    );
    els.forEach(el => io.observe(el));
    const safety = setTimeout(() => els.forEach(el => el.classList.add('in')), 1500);
    return () => { io.disconnect(); clearTimeout(safety); };
  }, []);
}

const DATA_ROWS = [
  { item: 'Thickness, product without underlayment', standard: 'ISO 24337', requirement: '± 0.13mm', result: 'Pass' },
  { item: 'Sizes, tolerance', standard: 'ISO 24337', requirement: 'Length: ± 1mm\nWidth: ± 0.15mm', result: 'Pass' },
  { item: 'Dimensional stability (6h, 80°C)', standard: 'ISO 23999', requirement: '≤ 0.2% (305mm)', result: 'Pass' },
  { item: 'Curl (6h, 80°C)', standard: 'ISO 23999', requirement: '≤ 2mm', result: 'Pass' },
  { item: 'Peel strength', standard: 'EN 431', requirement: '≥ 20N (25mm, Wear layer 0.3mm)\n≥ 30N (25mm, Wear layer 0.5mm)', result: 'Pass' },
  { item: 'Color fastness', standard: 'ASTM F1515', requirement: 'ΔE ≤ 8', result: 'Pass' },
  { item: 'Abrasion resistance', standard: 'ASTM D6040', requirement: '≥ 4500 cycles (Wear layer 0.3mm)\n≥ 6000 cycles (Wear layer 0.5mm)', result: 'Pass' },
  { item: 'Opening between elements', standard: 'ISO 24337', requirement: 'Average ≤ 0.1mm\nIndividual value ≤ 0.2mm', result: 'Pass' },
  { item: 'Height difference', standard: 'ISO 24337', requirement: 'Average ≤ 0.1mm\nIndividual value ≤ 0.15mm', result: 'Pass' },
  { item: 'Gloss', standard: 'ISO 2813', requirement: '4–8 GU', result: 'Pass' },
  { item: 'Scratch resistance', standard: 'ISO 1518', requirement: '≥ 2000 grams', result: 'Pass' },
  { item: 'Residual indentation', standard: 'ASTM F1914', requirement: '≤ 3%', result: 'Pass' },
  { item: 'Static load', standard: 'ASTM F970-17', requirement: 'Pass, 720 psi at 0.007"', result: 'Pass' },
  { item: 'Impact resistance', standard: 'ASTM F1265-03a', requirement: 'No break or crack', result: 'Pass' },
  { item: 'Resistance to chemical', standard: 'ASTM F925-13', requirement: 'No more than a slight change in surface dulling, surface attack or staining', result: 'Pass' },
  { item: 'Impact insulation class (IIC)', standard: 'ASTM E492-09\nASTM E989-18', requirement: 'N/A', result: '69 dB' },
  { item: 'Sound transmission class (STC)', standard: 'ASTM E90-09\nASTM E413-16', requirement: 'N/A', result: '68 dB' },
  { item: 'Burning behavior', standard: 'EN 13501', requirement: 'Bfl-S1', result: '—' },
  { item: 'Slip resistance', standard: 'EN 13893', requirement: 'Class DS', result: '—' },
  { item: 'Static Coefficient of friction', standard: 'ASTM D2047-17', requirement: 'Dry: 0.42\nWet: 0.61', result: '—' },
  { item: 'Thermal resistance', standard: 'EN 12667', requirement: '0.028 – 0.053 (W/m²·K)', result: '—' },
  { item: 'Formaldehyde emission', standard: 'EN 717', requirement: 'Class E1', result: '—' },
  { item: 'Locking system', standard: '—', requirement: 'Uniclic', result: '—' },
];

export default function TechnicalDataSheetPage() {
  useReveal();
  return (
    <main style={{ paddingTop: 76 }}>
      <div id="refresh-top" />

      {/* Header */}
      <section style={{ background: 'var(--ivory-soft)', padding: '56px 0 40px', borderBottom: '1px solid var(--rule)' }}>
        <div className="page-container">
          <Link
            to="/"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--ash)', letterSpacing: '.08em', textDecoration: 'none', marginBottom: 24 }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--ink)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--ash)'}
          >
            <ArrowLeft size={12} /> Back to Home
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(59,95,122,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText size={18} style={{ color: 'var(--indigo)' }} />
            </div>
            <p className="eyebrow eyebrow-gold">Technical Documents</p>
          </div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(36px,6vw,64px)', lineHeight: 1.0, textWrap: 'balance' }}>
            LVP Technical Data Sheet
          </h1>
          <p style={{ fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--ash)', marginTop: 12 }}>
            Click Lock SPC Flooring · Love3 Products, Inc.
          </p>
          {/* Download button */}
          <div style={{ marginTop: 20 }}>
            <a
              href="/lvp-technical-data-sheet.pdf"
              download="LVP-Technical-Data-Sheet-ContractorRob.pdf"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600,
                letterSpacing: '.1em', textTransform: 'uppercase',
                color: 'var(--ink)', background: 'var(--saffron)',
                padding: '10px 20px', borderRadius: 'var(--r-sm)',
                textDecoration: 'none', transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--saffron-deep)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--saffron)'}
            >
              <Download size={13} /> Download as PDF
            </a>
          </div>
        </div>
      </section>

      {/* Table */}
      <section style={{ padding: '64px 0 96px' }}>
        <div className="page-container">
          <div style={{ overflowX: 'auto', borderRadius: 'var(--r-md)', border: '1px solid var(--rule)', boxShadow: '0 4px 24px rgba(22,21,18,.06)' }} data-reveal>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--sans)', fontSize: 14, minWidth: 600 }}>
              <thead>
                <tr style={{ background: 'var(--ink)', color: '#fff' }}>
                  {['Testing Items', 'Standard', 'Requirement', 'Result'].map(h => (
                    <th key={h} style={{
                      padding: '16px 20px',
                      textAlign: 'left',
                      fontWeight: 600,
                      letterSpacing: '.06em',
                      fontSize: 11,
                      textTransform: 'uppercase',
                      color: 'rgba(255,255,255,.75)',
                      whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DATA_ROWS.map((row, idx) => (
                  <tr
                    key={row.item}
                    style={{
                      background: idx % 2 === 0 ? '#fff' : 'var(--ivory-soft)',
                      borderBottom: '1px solid var(--rule)',
                    }}
                  >
                    <td style={{ padding: '14px 20px', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.45, maxWidth: 260 }}>{row.item}</td>
                    <td style={{ padding: '14px 20px', color: 'var(--ash)', fontFamily: 'var(--mono)', fontSize: 12, whiteSpace: 'pre-line', lineHeight: 1.55 }}>{row.standard}</td>
                    <td style={{ padding: '14px 20px', color: 'var(--ink-soft)', lineHeight: 1.55, whiteSpace: 'pre-line', maxWidth: 300 }}>{row.requirement}</td>
                    <td style={{ padding: '14px 20px', whiteSpace: 'nowrap' }}>
                      {row.result === 'Pass' ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 700,
                          color: '#2d7a4a', background: 'rgba(45,122,74,.1)',
                          padding: '3px 10px', borderRadius: 'var(--r-pill)',
                          letterSpacing: '.04em',
                        }}>✓ Pass</span>
                      ) : (
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 13, color: row.result === '—' ? 'var(--mist)' : 'var(--ink)', fontWeight: row.result !== '—' ? 600 : 400 }}>
                          {row.result}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={{ fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--ash)', marginTop: 16, lineHeight: 1.6 }} data-reveal>
            Testing conducted on Click Lock SPC Flooring in accordance with the standards listed above. Results on file at Love3 Products, Inc.
          </p>

          {/* Navigation between docs */}
          <div style={{ marginTop: 56, display: 'flex', gap: 16, flexWrap: 'wrap' }} data-reveal>
            <Link to="/technical/warranty" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              ← Warranty
            </Link>
            <Link to="/technical/installation-instructions" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Installation Instructions →
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
